import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { formatINR } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { Plus, Minus, ArrowLeft, ShieldCheck, Truck, Tag } from '@phosphor-icons/react';
import { PRODUCT } from '@/constants/testIds';
import Recommendations from '@/components/shop/Recommendations';

export default function ProductDetail() {
  const { id } = useParams();
  const { qtyOf, add, increment, decrement } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setError('Product not found'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-12 animate-pulse">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="aspect-square rounded-3xl bg-stone-100" />
        <div className="space-y-3">
          <div className="h-8 bg-stone-100 rounded w-2/3" />
          <div className="h-4 bg-stone-100 rounded w-1/3" />
          <div className="h-20 bg-stone-100 rounded" />
        </div>
      </div>
    </div>;
  }
  if (error || !product) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <h1 className="font-heading text-2xl text-stone-900">{error || 'Not found'}</h1>
      <Link to="/shop" className="inline-flex items-center gap-2 mt-4 text-olive-800 hover:text-olive-900"><ArrowLeft size={16}/> Back to shop</Link>
    </div>;
  }

  const qty = qtyOf(product.id);
  const hasDiscount = product.mrp && product.mrp > product.price;
  const discount = hasDiscount ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-olive-800 hover:text-olive-900 mb-6"><ArrowLeft size={16}/> Back to shop</Link>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        <div className="space-y-3">
          <div className="aspect-square rounded-3xl overflow-hidden bg-cream-100 ring-1 ring-stone-200 relative">
            <img src={product.image_url || '/placeholder.png'} alt={product.name} className="w-full h-full object-cover" />
            {hasDiscount && (
              <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-terracotta-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                <Tag size={12} weight="fill" /> {discount}% OFF
              </span>
            )}
          </div>
          {product.images && product.images[0] && (
            <div className="grid grid-cols-4 gap-2">
              <div className="aspect-square rounded-2xl overflow-hidden bg-cream-100 ring-1 ring-stone-200">
                <img src={product.image_url || '/placeholder.png'} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden bg-cream-100 ring-1 ring-stone-200">
                <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          )}
        </div>
        <div className="space-y-5">
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta-700 font-semibold">{product.category_slug?.replace(/-/g, ' ')}</p>
            <h1 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-1">{product.name}</h1>
            <p className="text-sm text-stone-500 mt-1">{product.unit}</p>
          </div>

          <div className="flex items-baseline gap-3">
            <span className="font-heading text-3xl text-stone-900">{formatINR(product.price)}</span>
            {hasDiscount && <span className="text-stone-400 line-through">{formatINR(product.mrp)}</span>}
            {hasDiscount && <span className="text-sm font-semibold text-terracotta-700">Save {formatINR(product.mrp - product.price)}</span>}
          </div>

          {product.description && (
            <p className="text-stone-600 leading-relaxed">{product.description}</p>
          )}

          <div className="pt-2">
            {qty === 0 ? (
              <button
                onClick={() => add(product, 1)}
                data-testid={PRODUCT.detailAddBtn}
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 transition-colors shadow"
              >Add to Cart</button>
            ) : (
              <div className="inline-flex items-center gap-3 bg-terracotta-600 text-white rounded-full">
                <button onClick={() => decrement(product.id)} className="w-12 h-12 grid place-items-center"><Minus size={18} weight="bold"/></button>
                <span className="text-base font-bold w-6 text-center">{qty}</span>
                <button onClick={() => increment(product.id)} className="w-12 h-12 grid place-items-center"><Plus size={18} weight="bold"/></button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 pt-4">
            <div className="flex items-center gap-2 p-3 rounded-2xl bg-olive-50 border border-olive-100">
              <Truck size={20} weight="duotone" className="text-olive-700"/>
              <div><p className="text-xs font-semibold text-stone-900">Flexible Delivery</p><p className="text-[11px] text-stone-500">By area & availability</p></div>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-2xl bg-terracotta-50 border border-terracotta-100">
              <ShieldCheck size={20} weight="duotone" className="text-terracotta-700"/>
              <div><p className="text-xs font-semibold text-stone-900">Cash on Delivery</p><p className="text-[11px] text-stone-500">No prepay</p></div>
            </div>
          </div>
        </div>
      </div>

      <Recommendations productId={product.id} title="You may also like" />
    </div>
  );
}
